/**
 * IPostProcessEffect - unified interface for post-processing effects supporting
 * both WebGL (postprocessing library) and WebGPU (TSL nodes) backends.
 */

import type { Effect } from 'postprocessing';
import type * as THREE from 'three';
import type { Node, WebGPURenderer } from 'three/webgpu';

/** Base configuration for all post-processing effects */
export interface BaseEffectConfig {
  type: string;
  /**
   * Stable identity for an effect entry in the authoring stack.
   * Defaults to `type` when omitted.
   */
  id?: string;
  enabled?: boolean;
  /**
   * Optional explicit stack order override.
   * Falls back to effect class `order` when omitted.
   */
  order?: number;
  /** Optional user-facing label shown in tooling. */
  label?: string;
}

/** Context provided to effects during creation */
export interface EffectContext {
  scene: THREE.Scene;
  camera: THREE.Camera;
  renderer: THREE.WebGLRenderer | WebGPURenderer;
  excludedObjects?: ReadonlySet<THREE.Object3D>;
  excludedLayer?: number;
  /** Stable exclusion mask texture reference for TSL graph build (WebGPU). */
  exclusionMaskTexture?: THREE.Texture | null;
  /** Stable shared scene depth texture reference for TSL graph build (WebGPU). */
  sharedSceneDepthTexture?: THREE.Texture | null;
  sharedExclusionMaskTexture?: THREE.Texture | null;
  sharedHasExclusions?: boolean;
}

/** WebGL-specific effect data */
export interface WebGLEffectData {
  effects: Effect[];
  passes?: any[]; // Additional passes like NormalPass
}

/** Context available during TSL node composition */
export interface WebGPUComposeContext {
  /** The TSL pass() node */
  scenePass: any;
  /** Scene pass depth texture node */
  depthNode: Node;
  /** Current output node (same as first parameter to compose) */
  outputNode: Node;
  /** Camera being rendered */
  camera: THREE.Camera;
  /** WebGPU renderer */
  renderer: WebGPURenderer;
  /** Get an MRT texture by name (e.g. 'normal', 'emissive') -- returns null if not requested */
  getMRTTexture: (name: string) => Node | null;
  /** Pre-pass for normals/depth (if any effect needs it) */
  prePass?: any;
  /** Shared exclusion mask texture (when provided by another effect, e.g. tone mapping). */
  exclusionMaskTexture?: THREE.Texture | null;
  /** Optional scene colour node with editor excluded layer omitted (WebGPU bloom source). */
  bloomInputNode?: Node | null;
  /** Optional depth node matching {@link bloomInputNode}. */
  bloomDepthNode?: Node | null;
}

/** WebGPU-specific effect node data */
export interface WebGPUEffectNode {
  node: Node;
  needsMRT?: boolean;
  mrtOutputs?: string[]; // e.g., ['emissive', 'normal']

  /**
   * Compose this effect's node into the pipeline output chain.
   * Receives the current output node and scene pass data, returns the new output node.
   * If not provided, the effect is assumed to handle its own rendering externally.
   *
   * @param outputNode - The current pipeline output node
   * @param context - Context data including scene pass, depth, camera, MRT textures
   * @returns The new output node after applying this effect
   */
  compose?: (outputNode: Node, context: WebGPUComposeContext) => Node;
}

/**
 * Unified interface for post-processing effects that work with both WebGL and WebGPU.
 *
 * Effects implement either WebGL methods, WebGPU methods, or both.
 * The pipeline will call the appropriate methods based on the active renderer.
 */
export abstract class IPostProcessEffect<TConfig extends BaseEffectConfig = BaseEffectConfig> {
  /** Unique identifier for this effect type */
  abstract readonly type: string;

  /** Rendering order (lower values render first) */
  abstract readonly order: number;

  /** Get default configuration for this effect */
  abstract getDefaultConfig(): TConfig;

  // ============= WebGL Implementation =============

  /**
   * Create WebGL effect instances using the postprocessing library.
   * @returns Effect array and optional additional passes (e.g., NormalPass for SSAO)
   */
  createWebGLEffect(config: TConfig, context: EffectContext): WebGLEffectData | undefined { return undefined; }

  /**
   * Apply configuration updates to existing WebGL effects.
   * Called when config changes without recreating the effect.
   */
  applyWebGLConfig(effectData: WebGLEffectData, config: TConfig): void {}

  // ============= WebGPU Implementation =============

  /**
   * Create WebGPU TSL node for this effect.
   * @returns TSL node and MRT requirements
   */
  createWebGPUNode(config: TConfig, context: EffectContext): WebGPUEffectNode | undefined { return undefined; }

  /**
   * Apply configuration updates to existing WebGPU node.
   * Called when config changes without rebuilding the pipeline.
   */
  applyWebGPUConfig(node: WebGPUEffectNode, config: TConfig): void {}

  /**
   * Indicates if this effect needs a pre-pass (WebGPU only).
   * Pre-pass renders normals/depth before the main scene pass.
   * @returns true if this effect requires a pre-pass
   */
  needsPrePass(): boolean { return false; }

  /**
   * Create and configure the pre-pass (WebGPU only).
   * Called once during pipeline build if needsPrePass() returns true.
   * @param scene - Scene to render
   * @param camera - Camera to use
   * @param config - Effect configuration
   * @returns Pre-pass object with normal/depth textures
   */
  createPrePass(scene: THREE.Scene, camera: THREE.Camera, config: TConfig): any { return undefined; }

  /**
   * Apply effect to scene context using pre-pass data (WebGPU only).
   * Called after pre-pass creation but before scene rendering.
   * Use this for effects like AO that modify material evaluation via builtinAOContext.
   * @param scenePass - The scene pass to modify
   * @param config - Effect configuration
   * @param context - Context with pre-pass data
   */
  applySceneContext(scenePass: any, config: TConfig, context: WebGPUComposeContext): void {}

  /**
   * Check if a config change requires pipeline rebuild (WebGPU only).
   * If not implemented, assumes all changes require rebuild.
   */
  requiresRebuild(oldConfig: TConfig, newConfig: TConfig): boolean { return false; }

  // ============= Renderer-level Configuration =============

  /**
   * Apply configuration changes that affect renderer-level state.
   * Called when config changes, before pipeline rebuild.
   * Use this for settings like tone mapping that modify the renderer itself.
   */
  applyRendererConfig(config: TConfig, renderer: THREE.WebGLRenderer | WebGPURenderer): void {}

  // ============= Pre-render Hook =============

  /**
   * Called before the main pipeline render.
   * Use this for effects that need separate render passes (e.g., ID buffer for outlines).
   *
   * @param context - Effect context with scene, camera, renderer, and object visibility sets
   */
  preRender(context: EffectContext & {
    hiddenObjects: ReadonlySet<THREE.Object3D>;
    excludedObjects: ReadonlySet<THREE.Object3D>;
  }): void {}

  // ============= Lifecycle =============

  /** Dispose of effect resources */
  dispose(): void {}
}

/**
 * Effect ordering constants for built-in effects.
 * Custom effects can use values between these to control ordering.
 */
